import React, { useState } from "react"
import { Alert, Anchor, Badge, Code, Collapse, Group, Stack, Table, Tabs, Text, Title, UnstyledButton } from "@mantine/core"
import { CodeHighlight } from "@mantine/code-highlight"
import {
  IconAlertTriangle,
  IconBrandDocker,
  IconChevronRight,
  IconCloud,
  IconExternalLink,
  IconFileCode,
  IconPlaneDeparture,
  IconRocket,
  IconServerCog,
  IconShip,
  IconSitemap,
  IconTerminal2,
  IconTrain
} from "@tabler/icons-react"
import { codeLanguage } from "../../../highlight"

const NWC_EXAMPLE = "nostr+walletconnect://PUBKEY?relay=wss://relay.example.com&secret=SECRET"
const LSC_EXAMPLE = "lightning+swapconnect://lightning-swap.com/?key=KEY&secret=SECRET"

type EnvVarRow = {
  name: string
  required: boolean
  description: string
}

const ENV_VARS: EnvVarRow[] = [
  {
    name: "NWC_URI",
    required: true,
    description: "The receive-only NWC code from step 1. Without it OpenReceive cannot issue an invoice."
  },
  {
    name: "LSC_URI_PRIMARY",
    required: false,
    description: "The LSC code from step 2. Set it only if you want to accept currencies other than Bitcoin."
  },
  {
    name: "LSC_URI_BACKUP",
    required: false,
    description: "A second swap provider, consulted only when the primary one is unreachable."
  }
]

type Snippet = {
  caption?: string
  language: string
  code: string
}

type Platform = {
  id: string
  label: string
  icon: React.ComponentType<{ size?: number }>
  badge: string
  blurb: string
  snippets: Snippet[]
  docs?: { label: string; url: string }
}

const PLATFORMS: Platform[] = [
  {
    id: "dotenv",
    label: ".env file",
    icon: IconFileCode,
    badge: "Local development",
    blurb:
      "The usual choice while you are building. Keep the file beside your app, add it to .gitignore before you paste anything into it, and never ship it inside a container image.",
    snippets: [
      {
        caption: ".env — server-only, never committed",
        language: "dotenv",
        code: [
          `NWC_URI=${NWC_EXAMPLE}`,
          `LSC_URI_PRIMARY=${LSC_EXAMPLE}`,
          "LSC_URI_BACKUP="
        ].join("\n")
      },
      {
        caption: "Load it",
        language: "bash",
        code: [
          "# Node 20.6+ reads the file itself — no dependency",
          "node --env-file=.env server.js",
          "",
          "# older Node: npm install dotenv, then import \"dotenv/config\" first thing",
          "",
          "# Rails: bundle add dotenv-rails --group \"development,test\"",
          "bin/rails server"
        ].join("\n")
      }
    ],
    docs: { label: "Node --env-file", url: "https://nodejs.org/api/cli.html#--env-fileconfig" }
  },
  {
    id: "shell",
    label: "Shell",
    icon: IconTerminal2,
    badge: "One-off",
    blurb:
      "Fine for a first run. The values disappear when the shell closes and they land in your shell history, so do not use this for a server that has to come back up on its own.",
    snippets: [
      {
        caption: "Export for this shell and everything it launches",
        language: "bash",
        code: [
          `export NWC_URI='${NWC_EXAMPLE}'`,
          `export LSC_URI_PRIMARY='${LSC_EXAMPLE}'`,
          "npm start          # or: bin/rails server"
        ].join("\n")
      },
      {
        caption: "Or scope it to a single command",
        language: "bash",
        code: [
          "# the leading space keeps the line out of bash history (HISTCONTROL=ignorespace)",
          ` NWC_URI='${NWC_EXAMPLE}' bin/rails server`
        ].join("\n")
      }
    ]
  },
  {
    id: "docker",
    label: "Docker",
    icon: IconBrandDocker,
    badge: "Containers",
    blurb:
      "Pass the codes in at run time. An ENV or ARG line in a Dockerfile is baked into the image and readable by anyone who can pull it — put nothing secret there.",
    snippets: [
      {
        caption: "docker run",
        language: "bash",
        code: [
          "# read from a file that never enters the image",
          "docker run --env-file .env -p 3000:3000 my-app",
          "",
          "# or one at a time, forwarded from the surrounding shell",
          "docker run -e NWC_URI -e LSC_URI_PRIMARY -p 3000:3000 my-app"
        ].join("\n")
      },
      {
        caption: "docker-compose.yml",
        language: "yaml",
        code: [
          "services:",
          "  web:",
          "    build: .",
          "    env_file: .env          # values live in the file, not in the repo",
          "    environment:",
          "      - LSC_URI_PRIMARY     # no \"=\": passes the value through from your shell"
        ].join("\n")
      }
    ],
    docs: {
      label: "Compose environment variables",
      url: "https://docs.docker.com/compose/how-tos/environment-variables/"
    }
  },
  {
    id: "kubernetes",
    label: "Kubernetes",
    icon: IconSitemap,
    badge: "Clusters",
    blurb:
      "Create a Secret, then hand the whole Secret to the container as environment variables. Do not inline the codes in the Deployment or a ConfigMap — those get committed.",
    snippets: [
      {
        caption: "Create the Secret",
        language: "bash",
        code: [
          "kubectl create secret generic openreceive \\",
          `  --from-literal=NWC_URI='${NWC_EXAMPLE}' \\`,
          `  --from-literal=LSC_URI_PRIMARY='${LSC_EXAMPLE}'`
        ].join("\n")
      },
      {
        caption: "deployment.yaml",
        language: "yaml",
        code: [
          "spec:",
          "  template:",
          "    spec:",
          "      containers:",
          "        - name: web",
          "          image: my-app:latest",
          "          envFrom:",
          "            - secretRef:",
          "                name: openreceive"
        ].join("\n")
      }
    ],
    docs: {
      label: "Kubernetes Secrets",
      url: "https://kubernetes.io/docs/concepts/configuration/secret/"
    }
  },
  {
    id: "systemd",
    label: "systemd",
    icon: IconServerCog,
    badge: "Your own server",
    blurb:
      "For an app you run yourself on a VPS or bare metal. Keep the values in a root-owned file with mode 600: Environment= lines written into the unit itself are readable by any user through systemctl show.",
    snippets: [
      {
        caption: "/etc/my-app/openreceive.env — mode 600, root-owned",
        language: "bash",
        code: [
          "sudo install -d -m 700 /etc/my-app",
          "sudo install -m 600 /dev/null /etc/my-app/openreceive.env",
          "sudo tee /etc/my-app/openreceive.env >/dev/null <<'EOF'",
          `NWC_URI=${NWC_EXAMPLE}`,
          `LSC_URI_PRIMARY=${LSC_EXAMPLE}`,
          "EOF"
        ].join("\n")
      },
      {
        caption: "/etc/systemd/system/my-app.service",
        language: "ini",
        code: [
          "[Service]",
          "EnvironmentFile=/etc/my-app/openreceive.env",
          "ExecStart=/usr/bin/node /srv/my-app/server.js",
          "Restart=always",
          "",
          "# sudo systemctl daemon-reload && sudo systemctl restart my-app"
        ].join("\n")
      }
    ],
    docs: {
      label: "systemd EnvironmentFile",
      url: "https://www.freedesktop.org/software/systemd/man/latest/systemd.exec.html#EnvironmentFile="
    }
  },
  {
    id: "kamal",
    label: "Kamal",
    icon: IconShip,
    badge: "Rails 8 deploys",
    blurb:
      "Name the variables in config/deploy.yml and Kamal reads their values from .kamal/secrets at deploy time, then injects them into the container. Keep .kamal/secrets out of git.",
    snippets: [
      {
        caption: "config/deploy.yml",
        language: "yaml",
        code: [
          "env:",
          "  secret:",
          "    - NWC_URI",
          "    - LSC_URI_PRIMARY",
          "    - LSC_URI_BACKUP"
        ].join("\n")
      },
      {
        caption: ".kamal/secrets — read from your shell or a password manager",
        language: "bash",
        code: [
          "NWC_URI=$NWC_URI",
          "LSC_URI_PRIMARY=$LSC_URI_PRIMARY",
          "",
          "# then ship it",
          "kamal deploy"
        ].join("\n")
      }
    ],
    docs: {
      label: "Kamal environment variables",
      url: "https://kamal-deploy.org/docs/configuration/environment-variables/"
    }
  },
  {
    id: "heroku",
    label: "Heroku",
    icon: IconCloud,
    badge: "PaaS",
    blurb:
      "Config vars are stored on the app and injected into every dyno. Setting one restarts the app, so expect a brief redeploy.",
    snippets: [
      {
        caption: "Set and verify",
        language: "bash",
        code: [
          "heroku config:set \\",
          `  NWC_URI='${NWC_EXAMPLE}' \\`,
          `  LSC_URI_PRIMARY='${LSC_EXAMPLE}' \\`,
          "  --app my-app",
          "",
          "heroku config:get NWC_URI --app my-app"
        ].join("\n")
      }
    ],
    docs: { label: "Heroku config vars", url: "https://devcenter.heroku.com/articles/config-vars" }
  },
  {
    id: "fly",
    label: "Fly.io",
    icon: IconPlaneDeparture,
    badge: "PaaS",
    blurb:
      "Secrets are encrypted at rest and appear as environment variables when the machine boots; setting one triggers a rolling restart. The [env] block in fly.toml is committed to git — keep these two out of it.",
    snippets: [
      {
        caption: "Set them",
        language: "bash",
        code: [
          "fly secrets set \\",
          `  NWC_URI='${NWC_EXAMPLE}' \\`,
          `  LSC_URI_PRIMARY='${LSC_EXAMPLE}'`,
          "",
          "fly secrets list   # names and digests only — values are write-only"
        ].join("\n")
      }
    ],
    docs: { label: "Fly.io secrets", url: "https://fly.io/docs/apps/secrets/" }
  },
  {
    id: "render",
    label: "Render",
    icon: IconRocket,
    badge: "PaaS",
    blurb:
      "Add them under Environment on the service, or in an Environment Group shared by several services. If you keep infrastructure in render.yaml, declare the name with sync: false so the value is entered in the dashboard instead of living in the repo.",
    snippets: [
      {
        caption: "render.yaml",
        language: "yaml",
        code: [
          "services:",
          "  - type: web",
          "    name: my-app",
          "    envVars:",
          "      - key: NWC_URI",
          "        sync: false      # set the value in the dashboard, not in git",
          "      - key: LSC_URI_PRIMARY",
          "        sync: false"
        ].join("\n")
      }
    ],
    docs: {
      label: "Render environment variables",
      url: "https://render.com/docs/configure-environment-variables"
    }
  },
  {
    id: "railway",
    label: "Railway",
    icon: IconTrain,
    badge: "PaaS",
    blurb:
      "Variables belong to one service in one environment. Set them again in each environment you deploy — production and staging do not share values. Changing one redeploys the service.",
    snippets: [
      {
        caption: "Set and list",
        language: "bash",
        code: [
          "railway variables \\",
          `  --set "NWC_URI=${NWC_EXAMPLE}" \\`,
          `  --set "LSC_URI_PRIMARY=${LSC_EXAMPLE}"`,
          "",
          "railway variables   # what the service will actually see"
        ].join("\n")
      }
    ],
    docs: { label: "Railway variables", url: "https://docs.railway.com/guides/variables" }
  }
]

export const EnvVarPlatforms: React.FC = () => {
  const [platformId, setPlatformId] = useState<string>(PLATFORMS[0].id)
  const [howToOpen, setHowToOpen] = useState(false)

  return (
    <Stack gap="md">
      <Table.ScrollContainer minWidth={520}>
        <Table withTableBorder withColumnBorders className="or-envvar-table">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Variable</Table.Th>
              <Table.Th w={110}>Required</Table.Th>
              <Table.Th>Value</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {ENV_VARS.map((row) => (
              <Table.Tr key={row.name}>
                <Table.Td>
                  <Code fz="sm" className="or-inline-code">
                    {row.name}
                  </Code>
                </Table.Td>
                <Table.Td>
                  <Badge variant={row.required ? "filled" : "light"} color={row.required ? "orGreen" : "gray"}>
                    {row.required ? "Required" : "Optional"}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">
                    {row.description}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      <UnstyledButton
        className="or-envvar-howto-toggle"
        onClick={() => setHowToOpen((open) => !open)}
        aria-expanded={howToOpen}
      >
        <IconChevronRight
          size={18}
          className="or-envvar-howto-chevron"
          data-open={howToOpen || undefined}
        />
        <Text span fw={600}>
          How do I set environment variables?
        </Text>
      </UnstyledButton>

      <Collapse expanded={howToOpen}>
        <Stack gap="md">
          <Alert
            variant="light"
            color="orange"
            radius="md"
            icon={<IconAlertTriangle size={18} />}
            title="Server side only"
          >
            These URIs are credentials. Read them on the server, and keep them out of browser bundles
            (anything prefixed <Code fz="sm">NEXT_PUBLIC_</Code> or <Code fz="sm">VITE_</Code>), out of
            logs, and out of git. The NWC code cannot spend from your wallet, but anyone holding it can
            issue invoices in your name.
          </Alert>

          <Tabs
            value={platformId}
            onChange={(value) => setPlatformId(value || PLATFORMS[0].id)}
            variant="pills"
            color="orGreen"
            keepMounted={false}
            className="or-envvar-tabs"
          >
            <Tabs.List mb="lg">
              {PLATFORMS.map((platform) => {
                const PlatformIcon = platform.icon
                return (
                  <Tabs.Tab
                    key={platform.id}
                    value={platform.id}
                    leftSection={<PlatformIcon size={16} />}
                  >
                    {platform.label}
                  </Tabs.Tab>
                )
              })}
            </Tabs.List>

            {PLATFORMS.map((platform) => (
              <Tabs.Panel key={platform.id} value={platform.id}>
                <Stack gap="sm">
                  <Group gap="sm" align="center">
                    <Title order={4} className="or-envvar-platform-title">
                      {platform.label}
                    </Title>
                    <Badge variant="light" color="gray">
                      {platform.badge}
                    </Badge>
                  </Group>
                  <Text c="dimmed" className="or-envvar-blurb">
                    {platform.blurb}
                  </Text>

                  {platform.snippets.map((snippet) => (
                    <div key={snippet.caption || snippet.code.slice(0, 24)}>
                      {snippet.caption ? (
                        <Text size="sm" fw={600} mb={6}>
                          {snippet.caption}
                        </Text>
                      ) : null}
                      <CodeHighlight
                        code={snippet.code}
                        language={codeLanguage(snippet.language)}
                        withCopyButton
                        radius="md"
                      />
                    </div>
                  ))}

                  {platform.docs ? (
                    <Anchor
                      href={platform.docs.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="sm"
                    >
                      {platform.docs.label} <IconExternalLink size={14} />
                    </Anchor>
                  ) : null}
                </Stack>
              </Tabs.Panel>
            ))}
          </Tabs>
        </Stack>
      </Collapse>
    </Stack>
  )
}
